/* eslint-disable max-len */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isUndefined } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import parser from 'components/ScalarSpreadsheet/utilities/parser';
import {
  CellValue,
  EvaluatedCell,
  EvaluatedCellMap,
  EvaluatedCellSheetMap,
  ICell,
  ScalarSpreadsheetConfigDataInterface,
  SheetNameDataMap,
} from 'models/interfaces/spreadsheet-types/core-types';
import { IWorkbook } from 'models/interfaces/spreadsheet-types/workbook-types';
import { VARIABLE_VALUE } from 'pages/CapTable/cap-table/utilities/variablePropertiesUtils';
import { useTableValidation } from 'services/hooks';
import { convertedColors } from '../constants';

const useWorkbook = (spreadsheets?: ScalarSpreadsheetConfigDataInterface<any, any, any, any>[]) => {
  const { validateCells } = useTableValidation();
  const [cells, setCells] = useState<EvaluatedCellSheetMap>();
  const [data, setData] = useState<SheetNameDataMap>();
  // The sheets that produced the current `cells`, committed in the same batch as
  // setCells so consumers can tell a settled build from one still evaluating.
  const [evaluatedSpreadsheets, setEvaluatedSpreadsheets] = useState<ScalarSpreadsheetConfigDataInterface[]>();
  const [workbook, setWorkbook] = useState<IWorkbook>();
  // this is the active cell in the workbook, not just a single sheet
  // This is the active cell in the workbook - meaning the cell that is currently being clicked on
  const [workbookActiveCell, setWorkbookActiveCell] = useState<ICell | null>(null);
  const spreadsheetsRef = useRef<ScalarSpreadsheetConfigDataInterface[] | null>(null);
  const editRevisionRef = useRef(0);

  // input is an array of ScalarSpreadsheetData

  const onChange = useCallback(
    (cell: EvaluatedCell, expr: CellValue, customFn?: (changes: EvaluatedCell[]) => void) => {
      if (!cells) {
        enqueueSnackbar('Error evaluating Cells', { variant: 'error' });
      }
      const changeList = cell.sheet.afterCellChanged(
        [{ cell, value: expr }],
        cell.sheet.cells as EvaluatedCellMap,
        cell.sheet.rowConfig,
        cell.sheet.tableData,
        cells
      );

      const changes = changeList.flatMap(({ cell: changeCell, value }) => changeCell.onChange(value, cells));

      if (customFn) {
        customFn(changes);
      }

      const sheetSet = changes
        .filter(Boolean)
        .reduce((sofar, change) => {
          sofar.add(change.sheet);
          return sofar;
        }, new Set<ScalarSpreadsheetConfigDataInterface>());

      const reversedData: any[] = [];

      let linkedChanges: EvaluatedCell[] = [];
      Array.from(sheetSet).forEach(sheet => {
        const sheetChanges = changeList.map(change => change.cell).filter(c => c.sheet?.name === sheet.name);
        const linkedSheetChanges = cell.getLinkedCells?.() || [];
        const linkedAndSheetChanges = [...sheetChanges, ...linkedSheetChanges];
        linkedChanges = [...linkedChanges, ...linkedSheetChanges];
        sheet.applyChangeConditions(linkedAndSheetChanges, cells, onChange);
        const cellsToValidate = sheetChanges;
        // variable cells should not be validated as they do not yield an accurate value but instead are determined by other cells
        validateCells({
          cellsToValidate: cellsToValidate.filter(
            cell => (isUndefined(cell.hidden) || cell.hidden === false) && cell.value !== VARIABLE_VALUE
          ),
          useOriginalCell: true,
          tableData: sheet.tableData,
          customValidations: sheet.customValidations,
          parsedColumns: sheet.cells,
        });

        if (sheet.reverseParser) {
          const reversed = sheet.reverseParse();
          reversedData.push(reversed);
        }
      });

      const allChanges = new Set([...changes, ...linkedChanges]);

      if (allChanges.size) {
        editRevisionRef.current += 1;
      }
      Array.from(allChanges).forEach(change => change?.dispatchEvent?.());
      return reversedData;
    },
    [cells, validateCells]
  );

  const areCellsValid = useCallback(
    (includeSheetsWithInvalidCells = false, isSaving = false) => {
      let sheetsWithInvalidCells: EvaluatedCell[][] = [];
      const isValid = (cell: EvaluatedCell) => cell.isValid || isUndefined(cell.isValid);
      if (!spreadsheetsRef.current) {
        return false;
      }
      spreadsheetsRef.current
        .filter(sheet => sheet.shouldValidate)
        .forEach(sheet => {
          validateCells({
            cellsToValidate:
              sheet?.cells &&
              Object.values(sheet.cells).filter(cell => !cell.readOnly || (cell as any).validateWhenReadOnly),
            useOriginalCell: true,
            customValidations: sheet.customValidations,
            parsedColumns: sheet.cells,
            tableData: sheet.tableData,
            isSaving,
            // ... other arguments
          });
        });

      const areAllCellsValid = cells ? Object.values(cells).every(sheet => Object.values(sheet).every(isValid)) : true;

      if (!areAllCellsValid && cells) {
        sheetsWithInvalidCells = Object.values(cells)
          .map(sheet => Object.values(sheet).filter(cell => !isValid(cell)))
          .filter(arr => arr.length);
      }

      if (includeSheetsWithInvalidCells) {
        return { areAllCellsValid, sheetsWithInvalidCells };
      }

      return areAllCellsValid;
    },
    [cells, validateCells]
  );

  const evaluateSpreadsheets = useCallback(async (sheets: ScalarSpreadsheetConfigDataInterface[], isCancelled?: () => boolean) => {
    const tmpData: SheetNameDataMap = {};

    let evaluatedRevision: number;
    do {
      evaluatedRevision = editRevisionRef.current;
      // This pass must settle before deciding whether the model changed during it.
      // eslint-disable-next-line no-await-in-loop
      await Promise.all(sheets.map(sheet => sheet.evaluate()));
      // A superseded configuration must never publish or rebuild outgoing sheets.
      if (isCancelled?.()) {
        return;
      }
      if (evaluatedRevision !== editRevisionRef.current) {
        // Enter can reverse-parse an edit while another sheet is still evaluating.
        // Re-read the model rather than publishing the pre-edit column snapshot.
        sheets.forEach(sheet => sheet.refreshColumns());
      }
    } while (evaluatedRevision !== editRevisionRef.current);
    sheets.forEach(sheet => {
      if (sheet.name) {
        tmpData[sheet.name] = sheet;
      }
    });
    const tmpCells = parser(tmpData);
    setData(tmpData);
    setCells(tmpCells);
    setEvaluatedSpreadsheets(sheets);
    // add a setting to sheet to determine if it needs to be reverse parsed after loading
    sheets
      .filter(sheet => (sheet as any).reverseParseAfterLoad)
      .forEach(sheet => {
        sheet.reverseParse();
      });
    spreadsheetsRef.current = sheets;
  }, []);

  // Latest wins: a spreadsheets change can overlap an in-flight evaluate, and without
  // cancellation the slower run's setCells could land last and leave stale cells rendered.
  useEffect(() => {
    let cancelled = false;
    const getData = async () => {
      if (!spreadsheets) {
        throw new Error('Spreadsheets are not provided. Ensure that the spreadsheets prop is passed to useWorkbook.');
      }
      // NEED TO GUARANTEE ALL SPREADSHEETS HAVE UPDATED SCOPE
      await evaluateSpreadsheets(spreadsheets, () => cancelled);
      if (cancelled) {
        return;
      }
      const workbookObj = spreadsheets.reduce((sofar, sheet) => ({ ...sofar, [sheet.name ?? '']: sheet }), {
        evaluateSpreadsheets,
      });
      setWorkbook(workbookObj);
    };
    if (spreadsheets?.length && spreadsheets.every(spreadsheet => !!spreadsheet)) {
      getData();
    }
    return () => {
      cancelled = true;
    };
  }, [evaluateSpreadsheets, spreadsheets]);

  const updateWorkbookActiveCell = useCallback((newWorkbookActiveCell: EvaluatedCell | null) => {
    setWorkbookActiveCell(oldWorkBookActiveCell => {
      if (oldWorkBookActiveCell?.providers) {
        const providers = Array.from(oldWorkBookActiveCell.providers);
        providers.forEach(p => {
          p.setCellBackgroundColor?.(undefined);
          p.dispatchEvent?.();
        });
      }
      return newWorkbookActiveCell;
    });
  }, []);

  const workbookContextValue = useMemo(
    () => ({
      setWorkbookActiveCell,
      updateWorkbookActiveCell,
      workbookActiveCell,
    }),
    [workbookActiveCell, updateWorkbookActiveCell]
  );

  // when the workbookActiveCell changes,
  useEffect(() => {
    if (workbookActiveCell?.providers) {
      const providers = Array.from(workbookActiveCell.providers);
      providers.forEach((p, i) => {
        p.setCellBackgroundColor?.(convertedColors[i]);
        p.dispatchEvent?.();
      });
    }
  }, [workbookActiveCell]);

  return {
    areCellsValid,
    cells,
    data,
    evaluatedSpreadsheets,
    evaluateSpreadsheets,
    onChange,
    setCells,
    setData,
    workbook,
    workbookContextValue,
  };
};

export default useWorkbook;
